import { getApiBaseUrlByTenantCode, type TenantCode } from '@tectonics/commons/tenant';
import type { RiskSignals } from '../hooks/use-risk-signals';
import type { RegistrationOptions, CredentialResponse } from './webauthn';

interface EnrollmentInitiationPayload {
  readonly contract: string;
  readonly payment_type_code: string;
  readonly operation: string;
  readonly enrollment: {
    readonly country: string;
    readonly notification_url: string;
    readonly identification_type: string;
    readonly identification: string;
    readonly account_details: {
      readonly bank_id: string;
    };
    readonly fidoOptions: {
      readonly platform: string;
      readonly risk_signals: RiskSignals;
    };
  };
}

interface BankListItem {
  readonly code: string;
  readonly name: string;
  readonly logo_url?: string;
}

interface EnrollmentInitiationResponse {
  readonly redirect_url: string;
}

interface EnrollmentStatusResponse {
  readonly status: string;
  readonly enrollment?: {
    readonly enrollment_code?: string;
    readonly status?: string;
    readonly bank?: BankListItem;
    readonly identification?: string;
    readonly omit_cpf_field?: boolean;
    readonly omit_bank_field?: boolean;
    readonly registration_options?: RegistrationOptions;
    readonly intent_url?: string;
  };
}

interface EnrollmentAuthPayload {
  readonly contract: string;
  readonly payment_type_code: string;
  readonly operation: string;
  readonly enrollment: {
    readonly authentication: CredentialResponse;
  };
}

interface EnrollmentAuthResponse {
  readonly status: string;
  readonly payment_type?: string;
  readonly error?: string;
  readonly message?: string;
  readonly enrollment?: {
    readonly status?: string;
  };
}

function getBaseUrl(tenantCode: TenantCode): string {
  return getApiBaseUrlByTenantCode(tenantCode);
}

function getErrorMessage(data: unknown, fallback: string): string {
  if (typeof data === 'object' && data !== null && 'error' in data) {
    const error = data.error;
    if (error) return String(error);
  }
  return fallback;
}

export async function fetchBankList(
  contract: string,
  tenantCode: TenantCode,
): Promise<BankListItem[]> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment/bank-list?contract=${encodeURIComponent(contract)}&country_code=br&payment_type=pix-jsr`,
  );

  const data: unknown = await response.json();

  if (!response.ok || !Array.isArray(data)) {
    throw new Error(getErrorMessage(data, 'Falha ao carregar lista de bancos.'));
  }

  return data as BankListItem[];
}

export async function initiateEnrollment(
  payload: EnrollmentInitiationPayload,
  tenantCode: TenantCode,
): Promise<EnrollmentInitiationResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );

  const data: unknown = await response.json();

  if (
    !response.ok
    || typeof data !== 'object'
    || data === null
    || !('redirect_url' in data)
    || !data.redirect_url
  ) {
    throw new Error(getErrorMessage(data, 'Falha ao iniciar vincula\u00E7\u00E3o.'));
  }

  return data as EnrollmentInitiationResponse;
}

export async function pollEnrollmentStatus(
  contract: string,
  tenantCode: TenantCode,
): Promise<EnrollmentStatusResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment/query?contract=${encodeURIComponent(contract)}`,
  );

  const data: unknown = await response.json();

  if (!response.ok) {
    throw new Error(getErrorMessage(data, 'Falha ao verificar status.'));
  }

  return data as EnrollmentStatusResponse;
}

export async function confirmEnrollment(
  payload: EnrollmentAuthPayload,
  tenantCode: TenantCode,
): Promise<EnrollmentAuthResponse> {
  const baseUrl = getBaseUrl(tenantCode);
  const response = await fetch(
    `${baseUrl}cf/hosted-enrollment/authentication`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );

  const data: unknown = await response.json();

  if (!response.ok) {
    throw new Error(getErrorMessage(data, 'Falha ao confirmar vincula\u00E7\u00E3o.'));
  }

  return data as EnrollmentAuthResponse;
}
